import { z } from "zod";
import { QueryParams } from "../../httpClient";
import { MatchingStrategyEnum, UrgencyEnum } from "./common";

// ==================
// Query Parameter Types
// ==================

/** Query params for listing draft leads (Admin) */
export interface AdminListDraftLeadsQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
    /** Filter by status: pending, submitted, or all (default) */
    status?: "pending" | "submitted" | "all";
}

// ==================
// Public Request Body Schemas
// ==================

/** 
 * Request body for creating or updating a draft lead (Public)
 * - When creating: email is required, token should not be provided
 * - When updating: token is required, email is optional
 */
export const CreateOrUpdateDraftLeadRequestSchema = z.object({
    /** Token for updating existing draft (if provided, updates instead of creates) */
    token: z.string().optional(),
    /** Email address (required when creating new draft) */
    email: z.string().email().optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    /** Project title (max 255 characters) */
    project_title: z.string().max(255).optional(),
    project_description: z.string().optional(),
    /** Shopify store URL (must be valid URL) */
    store_url: z.string().url().optional(),
    /** Brand/company name */
    brand_name: z.string().optional(),
    /** Expert ID to work with */
    selected_expert_id: z.number().int().positive().optional(),
    /** Packaged service ID (must belong to selected expert) */
    selected_packaged_service_id: z.number().int().positive().optional(),
    urgency: UrgencyEnum.optional(),
    matching_strategy: MatchingStrategyEnum.optional(),
    /** Budget range ID */
    project_value_range_id: z.number().int().positive().optional(),
    /** Browser timezone (IANA, e.g. America/New_York) */
    timezone: z.string().max(64).optional(),
});

export type CreateOrUpdateDraftLeadRequest = z.infer<typeof CreateOrUpdateDraftLeadRequestSchema>;

// ==================
// Admin Request Body Schemas
// ==================

/** Request body for creating a draft lead (Admin) */
export const AdminCreateDraftLeadRequestSchema = z.object({
    email: z.string().email(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    project_title: z.string().max(255).optional(),
    project_description: z.string().optional(),
    store_url: z.string().url().optional(),
    brand_name: z.string().optional(),
    selected_expert_id: z.number().int().positive().optional(),
    selected_packaged_service_id: z.number().int().positive().optional(),
    urgency: UrgencyEnum.optional(),
    matching_strategy: MatchingStrategyEnum.optional(),
    project_value_range_id: z.number().int().positive().optional(),
});

export type AdminCreateDraftLeadRequest = z.infer<typeof AdminCreateDraftLeadRequestSchema>;

/** Request body for updating a draft lead (Admin) */
export const AdminUpdateDraftLeadRequestSchema = z.object({
    email: z.string().email().optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    project_title: z.string().max(255).optional(),
    project_description: z.string().optional(),
    store_url: z.string().url().optional(),
    brand_name: z.string().optional(),
    selected_expert_id: z.number().int().positive().optional(),
    selected_packaged_service_id: z.number().int().positive().optional(),
    urgency: UrgencyEnum.optional(),
    matching_strategy: MatchingStrategyEnum.optional(),
    project_value_range_id: z.number().int().positive().optional(),
});

export type AdminUpdateDraftLeadRequest = z.infer<typeof AdminUpdateDraftLeadRequestSchema>;
